import { useLocation, useNavigate } from "react-router-dom";
import AdminHeader from "../../Components/AdminHeader/AdminHeader";
import AdminNav from "../../Components/AdminNav/AdminNav";
import { IMAGE_URL } from "../../path";
import { FormEvent, useEffect, useState } from "react";
import { editCoreCapability } from "../../actions/HomePageImages";

function EditCoreCapability() {
    const location = useLocation();
    const navigate = useNavigate();

    const user = location.state?.user;
    const capability = location.state?.capability;

    const [isCollapsed, setIsCollapsed] = useState(true);

    const [topText, setTopText] = useState(
        capability?.top_text ?? ""
    );

    const [bottomText, setBottomText] = useState(
        capability?.bottom_text ?? ""
    );

    const [rightText, setRightText] = useState(
        capability?.right_text ?? ""
    );

    const [position, setPosition] = useState(
        capability?.position ?? ""
    );

    const [imageFile, setImageFile] = useState<File | null>(null);

    const [previewUrl, setPreviewUrl] = useState(
        capability?.image
            ? `${IMAGE_URL}${capability.image}`
            : ""
    );

    const [imageError, setImageError] = useState("");
    const [submitMessage, setSubmitMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!location.state?.user || !location.state?.capability) {
            navigate("/dashboard/home/images");
        }
    }, [location.state, navigate]);

    const validateImage = (file: File): string | null => {
        const validTypes = [
            "image/jpeg",
            "image/png",
            "image/webp",
        ];

        if (!validTypes.includes(file.type)) {
            return "Only JPG, PNG or WebP files are allowed.";
        }

        if (file.size > 2 * 1024 * 1024) {
            return "File size must be less than or equal to 2MB.";
        }

        return null;
    };

    const handleImageChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = e.target.files?.[0];

        if (!file) {
            return;
        }

        const error = validateImage(file);

        if (error) {
            setImageError(error);
            setImageFile(null);
            return;
        }

        if (previewUrl && previewUrl.startsWith("blob:")) {
            URL.revokeObjectURL(previewUrl);
        }

        const objectUrl = URL.createObjectURL(file);

        setImageError("");
        setImageFile(file);
        setPreviewUrl(objectUrl);
    };

    const removeSelectedImage = () => {
        if (previewUrl && previewUrl.startsWith("blob:")) {
            URL.revokeObjectURL(previewUrl);
        }

        setImageFile(null);

        setPreviewUrl(
            capability?.image
                ? `${IMAGE_URL}${capability.image}`
                : ""
        );

        setImageError("");
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (
            !topText ||
            !bottomText ||
            !rightText ||
            !position
        ) {
            setSubmitMessage(
                "Please fill in all required fields."
            );
            return;
        }

        setIsSubmitting(true);
        setSubmitMessage("");

        try {
            await editCoreCapability({
                id: capability.id,
                top_text: topText,
                bottom_text: bottomText,
                right_text: rightText,
                position: Number(position),
                image: imageFile ?? undefined,
            });

            navigate("/dashboard/home/images", {
                state: { user },
            });
        } catch (error) {
            console.error("Update failed:", error);

            setSubmitMessage(
                "Failed to update core capability. Please try again."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        return () => {
            if (previewUrl && previewUrl.startsWith("blob:")) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    if (!capability) {
        return null;
    }

    return (
        <div>
            <AdminHeader
                isCollapsed={isCollapsed}
                setIsCollapsed={setIsCollapsed}
                user={user}
            />

            <AdminNav
                collapsed={isCollapsed}
                setCollapsed={setIsCollapsed}
                user={user}
            />

            <div
                className={`main-admin-page ${
                    isCollapsed ? "collapsed" : "expanded"
                }`}
            >
                <div className="main-admin-page-inner">
                    <form onSubmit={handleSubmit}>
                        <div className="content-form-field">
                            <h2>Edit Core Capability</h2>
                        </div>

                        <div className="content-form-field">
                            <input
                                type="text"
                                className="input-email"
                                placeholder="Top Text"
                                value={topText}
                                onChange={e =>
                                    setTopText(e.target.value)
                                }
                                required
                            />
                        </div>

                        <div className="content-form-field">
                            <input
                                type="text"
                                className="input-email"
                                placeholder="Bottom Text"
                                value={bottomText}
                                onChange={e =>
                                    setBottomText(e.target.value)
                                }
                                required
                            />
                        </div>

                        <div className="content-form-field">
                            <label>Right Text</label>

                            <textarea
                                className="input-email"
                                rows={7}
                                value={rightText}
                                onChange={e =>
                                    setRightText(e.target.value)
                                }
                                required
                            />
                        </div>

                        <div className="content-form-field">
                            <input
                                type="number"
                                className="input-email"
                                placeholder="Position"
                                value={position}
                                onChange={e =>
                                    setPosition(e.target.value)
                                }
                                min="1"
                                required
                            />
                        </div>

                        <div className="content-form-field">
                            <label>
                                <strong>Image</strong>
                            </label>

                            <br />

                            <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.webp"
                                onChange={handleImageChange}
                                style={{
                                    margin: "10px 0",
                                }}
                            />

                            {imageError && (
                                <div
                                    style={{
                                        color: "red",
                                    }}
                                >
                                    {imageError}
                                </div>
                            )}

                            {previewUrl && (
                                <div
                                    style={{
                                        marginTop: "10px",
                                    }}
                                >
                                    <img
                                        src={previewUrl}
                                        alt="Core Capability Preview"
                                        style={{
                                            maxWidth: "100%",
                                            maxHeight: "300px",
                                            border: "1px solid #ccc",
                                        }}
                                    />

                                    <br />

                                    {imageFile && (
                                        <button
                                            type="button"
                                            onClick={
                                                removeSelectedImage
                                            }
                                            style={{
                                                marginTop: "5px",
                                            }}
                                        >
                                            Remove Selected Image
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <br />

                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={isSubmitting}
                        >
                            {isSubmitting
                                ? "Saving..."
                                : "Save Changes"}
                        </button>

                        {submitMessage && (
                            <div
                                style={{
                                    marginTop: "10px",
                                    color: "red",
                                }}
                            >
                                {submitMessage}
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
}

export default EditCoreCapability;
